import {
    Page,
    StubPage,
    PageIdentifier,
    PageAuthors,
    PageComment,
    PageAttributes,
    PageSubscriptionType,
    PageUser,
    DocTypeContent,
    PageType,
    Acl,
    Access,
    NavigationPage,
    NavigationPageType,
    PageRevision,
    PageAttachment,
    ToolboxType,
    Actuality,
    PageTocItem,
    UserPermissions,
} from '~shared/models';

import {UpdatePageRedirectArgs} from '~shared/schema/wiki/actions/update-page-details';

import {isNumber, isString, parsePath} from '~ui/utils';

import {PagesState} from './types';
interface Subscriber {
    id: number;
    is_cluster: boolean;
    user: PageUser;
}

export interface SetPageDetailsData
    extends Omit<Page, 'owner' | 'authors' | 'attributes' | 'comments' | 'acl' | 'content'> {
    toolboxType?: ToolboxType;
}

export type SetStubPageDetailsData = StubPage;

export interface SetPageAuthorsData {
    authors?: PageAuthors;
    slug: string;
}
export interface SetPageCommentsData {
    pageId: number;
    comments: PageComment[];
    prevCursor: string | null;
    nextCursor: string | null;
}
export interface SetPageRevisionsData {
    revisions: PageRevision[];
    id: number;
    nextCursor?: string;
    firstPage?: boolean;
}

export interface SetPageSubscribersData {
    results: Subscriber[];
    id: number;
    hasNext?: boolean;
    pageId: number;
}

export interface AddPageSubscribersData {
    results: Subscriber[];
    id: number;
}
export interface DeletePageSubscriberData {
    subscriberId: number;
    id: number;
}
export interface SetPageAttributesData {
    attributes?: PageAttributes;
    slug: string;
}

export interface SetPageBookmarkData {
    slug: string;
    bookmarkId?: number;
}

export interface SetPageSubscriptionData {
    slug: string;
    subscription?: PageSubscriptionType;
}

const setPageDetails = (state: PagesState, page: SetPageDetailsData) => {
    state.entities[page.slug] = Object.assign({}, state.entities[page.slug], page);

    if (page.id && !state.byId[page.id]) {
        state.byId[page.id] = page.slug;
    }
};

const setStubPageDetails = (state: PagesState, page: SetStubPageDetailsData) => {
    state.stubEntities[page.slug] = Object.assign({}, state.stubEntities[page.slug], page);
};

const setPageAttributes = (state: PagesState, page: SetPageAttributesData) => {
    const {attributes, slug} = page;

    const entity = state.entities[slug];

    if (!entity || !attributes) {
        return;
    }

    entity.attributes = Object.assign({}, entity.attributes, attributes);
};

const setPageAuthors = (state: PagesState, page: SetPageAuthorsData) => {
    const {authors, slug} = page;

    if (!authors) {
        return;
    }

    const {
        owner: {id},
        all,
    } = authors;

    const authorsIds = all.map((user) => user.id);

    const entity = state.entities[slug];

    if (!authorsIds.includes(id)) {
        authorsIds.unshift(id);
    }

    if (entity) {
        entity.owner = id;

        entity.authors = authorsIds;
    }

    const stubEntity = state.stubEntities[slug];

    if (stubEntity) {
        stubEntity.owner = id;

        stubEntity.authors = authorsIds;
    }
};

export interface SetPagePermissionsData {
    permissions?: UserPermissions[];
    slug: string;
}

const setPagePermissions = (state: PagesState, page: SetPagePermissionsData) => {
    const {permissions, slug} = page;

    if (!permissions) {
        return;
    }

    const entity = state.entities[slug];

    if (entity) {
        entity.userPermissions = permissions;
    }

    const stubEntity = state.stubEntities[slug];

    if (stubEntity) {
        stubEntity.userPermissions = permissions;
    }
};

const setPageComments = (state: PagesState, data: SetPageCommentsData) => {
    const {comments, pageId, prevCursor, nextCursor} = data;
    const slug = state.byId[pageId];

    if (!isString(slug)) {
        return;
    }

    const entity = state.entities[slug];

    if (!entity) {
        return;
    }

    const commentsSet = new Set(entity.comments);

    const commentsIds = comments.reduce<number[]>((acc, comment) => {
        if (!comment.is_deleted && !commentsSet.has(comment.id)) {
            acc.push(comment.id);
        }
        return acc;
    }, []);

    entity.comments = (entity.comments || []).concat(commentsIds);

    entity.commentsPagesData = {
        prevCursor,
        nextCursor,
    };
};
const setPageRevisions = (state: PagesState, data: SetPageRevisionsData) => {
    const {revisions, id} = data;
    const slug = state.byId[id];

    if (!isString(slug)) {
        return;
    }

    const entity = state.entities[slug];

    if (!entity) {
        return;
    }

    if (data.firstPage) {
        entity.revisions = revisions.map<number>((revision) => {
            return revision.id;
        });
    } else {
        const revisionsSet = new Set(entity.revisions);

        const revisionsIds = revisions.reduce<number[]>((acc, revision) => {
            if (!revisionsSet.has(revision.id)) {
                acc.push(revision.id);
            }
            return acc;
        }, []);

        entity.revisions = (entity.revisions || []).concat(revisionsIds);
    }

    entity.revisionsData = {...entity.revisionsData, ...{nextCursor: data.nextCursor}};
};

const setPageCommentsCount = (state: PagesState, slug: string, count: number) => {
    const entity = state.entities[slug];

    if (!entity) {
        return;
    }

    entity.attributes = Object.assign({}, entity.attributes, {
        comments_count: count,
    });
};

const decrementPageCommentsCount = (state: PagesState, slug: string) => {
    const currentCount = state.entities[slug]?.attributes?.comments_count || 0;
    const newCount = Math.max(currentCount - 1, 0);

    setPageCommentsCount(state, slug, newCount);
};

const incrementPageCommentsCount = (state: PagesState, slug: string) => {
    const currentCount = state.entities[slug]?.attributes?.comments_count || 0;
    const newCount = currentCount + 1;

    setPageCommentsCount(state, slug, newCount);
};

const setPageBookmark = (state: PagesState, data: SetPageBookmarkData) => {
    const {slug, bookmarkId} = data;

    const entity = state.entities[slug];

    if (!entity) {
        return;
    }

    entity.bookmarkId = bookmarkId;
};

const updatePagesSlugs = (state: PagesState, oldToNewSlugMap: Record<string, string>) => {
    const {entities} = state;
    const newById: Record<number, string> = {};
    const newEntities: Record<string, Page> = {};

    const newCurrentPageSlug = oldToNewSlugMap[state.current];

    if (newCurrentPageSlug) {
        state.current = newCurrentPageSlug;
    }

    for (const key in entities) {
        const entity = entities[key];

        if (!entity) continue;

        const newPageSlug = oldToNewSlugMap[entity.slug];

        if (newPageSlug) {
            entity.slug = newPageSlug;
        }

        newEntities[entity.slug] = entity;
        newById[entity.id] = entity.slug;
    }

    state.byId = newById;
    state.entities = newEntities;
};

const setPageSubscription = (state: PagesState, data: SetPageSubscriptionData) => {
    const {slug, subscription} = data;

    const entity = state.entities[slug];

    if (!entity) {
        return;
    }

    entity.subscription = subscription;
};

const setPageSubscribers = (state: PagesState, data: SetPageSubscribersData) => {
    const {results, id, hasNext, pageId} = data;
    const slug = state.byId[id];

    if (!isString(slug)) {
        return;
    }

    const entity = state.entities[slug];

    if (!entity) {
        return;
    }

    const subscribersSet = new Set(entity.subscribers);

    const subscribersIds = results.reduce<number[]>((acc, item) => {
        if (!subscribersSet.has(item.id)) {
            acc.push(item.id);
        }
        return acc;
    }, entity.subscribers || []);

    entity.subscribers = subscribersIds;

    entity.subscribersPagesData = {
        hasNext,
        pageId: pageId + 1,
    };
};

const addPageSubscribers = (state: PagesState, data: AddPageSubscribersData) => {
    const {results, id} = data;
    const slug = state.byId[id];

    if (!isString(slug)) {
        return;
    }

    const entity = state.entities[slug];

    if (!entity) {
        return;
    }

    const subscribersSet = new Set(entity.subscribers);
    const subscribersIds = entity.subscribers || [];

    const newSubscribersIds = results
        .filter((item) => !subscribersSet.has(item.id))
        .map((item) => item.id);

    entity.subscribers = newSubscribersIds.concat(subscribersIds);
};

const deletePageSubscriber = (state: PagesState, data: DeletePageSubscriberData) => {
    const {subscriberId, id} = data;
    const slug = state.byId[id];

    if (!isString(slug)) {
        return;
    }

    const entity = state.entities[slug];

    if (!entity) {
        return;
    }

    const subscribersIds = entity.subscribers?.filter((item) => {
        return item !== subscriberId;
    });

    entity.subscribers = subscribersIds;
};

const getSlugByIdentifier = (state: PagesState, identifier: PageIdentifier) => {
    const pageIdentity = {
        id: undefined,
        slug: undefined,
        ...identifier,
    };

    let slug = pageIdentity.slug;

    if (pageIdentity.id) {
        slug = state.byId[pageIdentity.id];
    }

    return slug;
};

export interface SetContentData {
    pageContent: DocTypeContent;
    slug: string;
}

const setPageContent = (state: PagesState, {slug, pageContent}: SetContentData) => {
    const entity = state.entities[slug];

    if (!entity) {
        return;
    }

    switch (pageContent.type) {
        case PageType.CloudPage:
            if (pageContent.content !== undefined) {
                const {
                    embed: {iframe_src: iframeSrc, edit_src: editSrc},
                    acl_management: aclManagement,
                    type,
                    filename,
                } = pageContent.content;

                entity.content = {
                    iframeSrc,
                    editSrc,
                    aclManagement,
                    type,
                    filename,
                };
            }

            break;
        case PageType.Page:
        case PageType.Wysiwyg:
            if (pageContent.content !== undefined) {
                entity.content = pageContent.content;
            }

            if (pageContent.html !== undefined) {
                entity.html = pageContent.html;
            }

            if (pageContent.translatedHtml !== undefined) {
                entity.translatedHtml = pageContent.translatedHtml;
            }
            break;
        default:
            if (pageContent.content !== undefined) {
                entity.content = pageContent.content;
            }
            break;
    }
};

export interface SetPageAclData {
    slug: string;
    acl?: Acl;
    access?: Access;
}

const setPageAcl = (state: PagesState, {slug, acl, access}: SetPageAclData) => {
    const page = state.entities[slug];
    const stubPage = state.stubEntities[slug];

    if (!acl) {
        if (page) {
            page.acl = undefined;
        }
        if (stubPage) {
            stubPage.acl = undefined;
        }
        return;
    }

    const {
        acl_type: aclType,
        break_inheritance: breakInheritance,
        inherits_from: inheritsFrom,
        is_readonly: isReadonly,
        users = [],
        groups = [],
    } = acl;

    const aclData = {
        users: users.map((user) => user.id),
        groups: groups
            .filter((group) => group.type !== 'department')
            .map((group) => group.identity),
        departments: groups
            .filter((group) => group.type === 'department')
            .map((dpt) => dpt.identity),
        aclType,
        breakInheritance,
        inheritsFrom,
        isReadonly,
    };

    if (page) {
        if (access) {
            page.access = {
                type: access.type,
                isReadonly: access.is_readonly,
                hasExternal: access.has_external,
                inheritsFrom: access.inherits_from,
            };
        }

        page.acl = aclData;
    }

    if (stubPage) {
        stubPage.acl = aclData;
    }
};

interface SetAclLoadingData {
    slug: string;
    isLoading: boolean;
}

const setPageAclLoading = (state: PagesState, {slug, isLoading}: SetAclLoadingData) => {
    const page = state.entities[slug];

    if (page) {
        page.isAclLoading = isLoading;
    }

    const stubPage = state.stubEntities[slug];

    if (stubPage) {
        stubPage.isAclLoading = isLoading;
    }
};

export interface SetAuthorsLoadingData {
    slug: string;
    isLoading: boolean;
}

export interface SetAuthorsError {
    slug: string;
    error: string;
}

const setPageAuthorsLoading = (state: PagesState, {slug, isLoading}: SetAuthorsLoadingData) => {
    const page = state.entities[slug];

    if (page) {
        page.isAuthorsLoading = isLoading;
    }

    const stubPage = state.stubEntities[slug];

    if (stubPage) {
        stubPage.isAuthorsLoading = isLoading;
    }
};

const setPageAuthorsError = (state: PagesState, {slug, error}: SetAuthorsError) => {
    const page = state.entities[slug];

    if (page) {
        page.authorsError = error;
    }

    const stubPage = state.stubEntities[slug];

    if (stubPage) {
        stubPage.authorsError = error;
    }
};

interface SetPageBackgroundData {
    id: number;
    background: number | null | undefined;
}

const setPageBackground = (state: PagesState, {id, background}: SetPageBackgroundData) => {
    const slug = state.byId[id];
    if (!slug) return;

    const page = state.entities[slug];
    if (!page) return;

    page.backgroundId = isNumber(background) ? background : undefined;
};

const setNavigationTreePageDetails = (state: PagesState, page: NavigationPage) => {
    if (
        isNumber(page.id) &&
        page.page_type &&
        // if the page is locked, we treat it as stub, since it
        // will not have real `page_type` and `title`
        page.type !== NavigationPageType.Locked
    ) {
        setPageDetails(state, {
            id: page.id,
            slug: page.slug,
            title: page.title,
            pageType: page.page_type,
        });
    } else {
        const pageData: StubPage = {
            title: page.title,
            slug: page.slug,
        };

        if (page.id) {
            pageData.id = page.id;
        }

        setStubPageDetails(state, pageData);
    }
};

interface SetRedirectLoadingData {
    id: number;
    isLoading: boolean;
}

const setPageRedirectLoading = (state: PagesState, {id, isLoading}: SetRedirectLoadingData) => {
    const slug = state.byId[id];

    if (!slug) {
        return;
    }

    const page = state.entities[slug];

    if (!page) {
        return;
    }

    page.isRedirectLoading = isLoading;
};

interface SetPageRedirectData {
    id: number;
    redirect?: UpdatePageRedirectArgs;
}

const setPageRedirect = (state: PagesState, {id, redirect}: SetPageRedirectData) => {
    const slug = state.byId[id];

    if (!slug) {
        return;
    }

    const page = state.entities[slug];

    if (!page) {
        return;
    }

    page.redirect = redirect && redirect.page ? redirect.page : undefined;
};

function getPageRedirectUrl(slug: string, forceRedirect?: boolean) {
    const {pathname, handler} = parsePath(window.location.pathname);

    const isPersonalCluster = pathname.startsWith('/~');
    let redirectUrl;

    // forceRedirect нужен чтобы средиректить после установки с хэндлером .settings
    if (!handler || forceRedirect) {
        redirectUrl = new URL(slug, window.location.origin);
        redirectUrl.searchParams.set('from', pathname);
    } else if (isPersonalCluster) {
        redirectUrl = new URL(`${slug}/${handler}`, window.location.origin);
        redirectUrl.searchParams.set('from', pathname);
    } else {
        redirectUrl = new URL(window.location.href);
        redirectUrl.searchParams.append('noredirect', '');
    }

    return `${redirectUrl.pathname}/?${redirectUrl.searchParams}`;
}

function getDeletedPageTitle(slug: string) {
    const index = slug.lastIndexOf('/');

    if (index === -1) {
        return slug;
    }

    return slug.slice(index + 1);
}

export interface RevisionsParams {
    revision1?: number;
    revision2?: number;
}

export interface PreviewYfmParams {
    previewYfm?: boolean;
}

function updateRevisionLocationParams(params: RevisionsParams) {
    const href = new URL(window.location.href);
    const {searchParams} = href;

    if (!params.revision1 && !params.revision2) {
        searchParams.delete('revision_1');
        searchParams.delete('revision_2');
        searchParams.delete('revision');
    } else if (params.revision1 && params.revision2) {
        searchParams.set('revision_1', String(params.revision1));
        searchParams.set('revision_2', String(params.revision2));
        searchParams.delete('revision');
    } else {
        searchParams.set('revision', String(params.revision1) || String(params.revision2));
        searchParams.delete('revision_1');
        searchParams.delete('revision_2');
    }

    window.history.replaceState(null, '', href.toString());
}

function updatePreviewYfmLocationParams(params: PreviewYfmParams) {
    const href = new URL(window.location.href);

    const {searchParams} = href;

    if (!params.previewYfm) {
        searchParams.delete('previewYfm');
    } else {
        searchParams.set('previewYfm', '1');
    }

    window.history.replaceState(null, '', href.toString());
}

export interface SetRevisionsData {
    id: number;
    revision1?: number;
    revision2?: number;
}

export interface SetPreviewYfmData {
    id: number;
    previewYfm?: boolean;
}

const setPageRevisionsData = (state: PagesState, data: SetRevisionsData) => {
    const {revision1, revision2, id} = data;
    const slug = state.byId[id];

    if (!isString(slug)) {
        return;
    }

    const entity = state.entities[slug];

    if (!entity) {
        return;
    }

    entity.revisionsData = Object.assign({}, entity.revisionsData, {
        revision1,
        revision2,
    });
};

export interface SetPageAttachmentsData {
    attachments: PageAttachment[];
    id: number;
    nextCursor?: string;
    firstPage?: boolean;
}

const setPageAttachments = (state: PagesState, data: SetPageAttachmentsData) => {
    const {attachments, id} = data;
    const slug = state.byId[id];

    if (!isString(slug)) {
        return;
    }

    const entity = state.entities[slug];

    if (!entity) {
        return;
    }

    if (data.firstPage) {
        entity.attachments = attachments.map<number>((attachment) => {
            return attachment.id;
        });
    } else {
        const attachmentsSet = new Set(entity.attachments);

        const attachmentsIds = attachments.reduce<number[]>((acc, attachment) => {
            if (!attachmentsSet.has(attachment.id)) {
                acc.push(attachment.id);
            }
            return acc;
        }, []);

        entity.attachments = (entity.attachments || []).concat(attachmentsIds);
    }

    entity.attachmentsData = {...entity.attachmentsData, nextCursor: data.nextCursor};
};

export interface DeletePageAttachmentData {
    pageId: number;
    id: number;
}

const deletePageAttachment = (state: PagesState, data: DeletePageAttachmentData) => {
    const {pageId, id} = data;
    const slug = state.byId[pageId];

    if (!isString(slug)) {
        return;
    }

    const entity = state.entities[slug];

    if (!entity) {
        return;
    }

    const index = (entity.attachments || []).indexOf(id);

    if (index !== -1) {
        entity.attachments?.splice(index, 1);
    }
};

export interface AttachFilesToPagetData {
    attachments: PageAttachment[];
    pageId: number;
}

const attachFilesToPage = (state: PagesState, data: AttachFilesToPagetData) => {
    const {pageId, attachments} = data;

    const slug = state.byId[pageId];

    if (!isString(slug)) {
        return;
    }

    const entity = state.entities[slug];

    if (!entity) {
        return;
    }

    const attachmentsSet = new Set(entity.attachments);

    const attachmentsIds = attachments.reduce<number[]>((acc, attach) => {
        if (!attachmentsSet.has(attach.id)) {
            acc.push(attach.id);
        }
        return acc;
    }, []);

    entity.attachments = attachmentsIds.concat(entity.attachments || []);
};

export interface SetAttachmentsLoadingData {
    slug: string;
    isLoading: boolean;
}

const setPageAttachmentsLoading = (
    state: PagesState,
    {slug, isLoading}: SetAttachmentsLoadingData,
) => {
    const page = state.entities[slug];

    if (!page) return;

    page.isAttachmentsLoading = isLoading;
};

export interface SetAttachmentsErrorData {
    slug: string;
    isError: boolean;
}

const setPageAttachmentsError = (state: PagesState, {slug, isError}: SetAttachmentsErrorData) => {
    const page = state.entities[slug];

    if (!page) return;

    page.isAttachmentsError = isError;
};

export interface SetPageActualityPayload {
    slug: string;
    actuality?: Actuality;
}

const setPageActuality = (state: PagesState, {slug, actuality}: SetPageActualityPayload) => {
    const page = state.entities[slug];
    if (!page) return;

    if (!actuality) {
        page.actuality = undefined;
        return;
    }

    const {
        marked_at: markedAt,
        status,
        user,
        comment,
        external_links: externalLinks,
        actual_pages: actualPages,
    } = actuality;

    page.actuality = {
        status,
        user,
        markedAt,
        comment,
        externalLinks,
        actualPages,
    };
};

export interface SetPageTocData {
    slug: string;
    toc?: PageTocItem[];
}

const setPageToc = (state: PagesState, data: SetPageTocData) => {
    const {slug, toc} = data;

    const entity = state.entities[slug];

    if (!entity) {
        return;
    }

    entity.toc = toc;
};

export {
    setPageDetails,
    setPageAttributes,
    setPageAuthors,
    setPageComments,
    decrementPageCommentsCount,
    incrementPageCommentsCount,
    setPageBookmark,
    updatePagesSlugs,
    setPageSubscription,
    setPageSubscribers,
    addPageSubscribers,
    deletePageSubscriber,
    getSlugByIdentifier,
    setPageContent,
    setPageAcl,
    setPageAuthorsLoading,
    setPageAuthorsError,
    setPageAclLoading,
    setPageBackground,
    setStubPageDetails,
    setNavigationTreePageDetails,
    setPageRedirectLoading,
    setPageRedirect,
    getPageRedirectUrl,
    getDeletedPageTitle,
    setPageRevisions,
    updateRevisionLocationParams,
    updatePreviewYfmLocationParams,
    setPageRevisionsData,
    setPageAttachments,
    deletePageAttachment,
    attachFilesToPage,
    setPageAttachmentsLoading,
    setPageAttachmentsError,
    setPageActuality,
    setPageToc,
    setPagePermissions,
};
